import {
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileAddOutlined,
  InboxOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  App,
  Button,
  Card,
  Col,
  Collapse,
  Divider,
  Image,
  InputNumber,
  Modal,
  Progress,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Upload,
  UploadFile,
} from 'antd';
import Title from 'antd/es/typography/Title';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../../../../../context/AuthProvider';
import {
  removePmContractSectionFile,
  updateField,
  updatePmContractSectionFile,
} from '../../../../../contextRedux/PMContractProject/PmContractModelProject';
import InterventionFileProjectService from '../../../../../services/interventionFileProject/InterventionFileProjectService';
import { RootState } from '../../../../../store';
import request from '../../../../../utils/request';
import './RaportTab.css';

const { Panel } = Collapse;
const { Dragger } = Upload;

interface TransportDetail {
  distance: number;
  time: number;
  cost: number;
  displacements: number;
  costPerKm: number;
  perTrip: {
    distance: number;
    duration: number;
    cost: number;
    distanceText: string;
    durationText: string;
  };
}

// Add interface for contact person
interface ContactPerson {
  name: string;
  role: string;
  phone: string;
}

// Define document section keys that match the backend model's "section" field
type DocumentSectionKey = 'projectOutline' | 'situationPlan' | 'otherDocuments';

const RaportTab: React.FC = () => {
  const dispatch = useDispatch();
  const [showLegend, setShowLegend] = useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [transportDetails, setTransportDetails] = useState<Record<string, TransportDetail>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [fileLists, setFileLists] = useState<Record<string, UploadFile<any>[]>>({
    projectOutline: [],
    situationPlan: [],
    otherDocuments: [],
  });

  // Define document sections
  const documentSections = [
    { key: 'projectOutline' as DocumentSectionKey, title: 'Schita Proiect' },
    { key: 'situationPlan' as DocumentSectionKey, title: 'Plan Situatie' },
    { key: 'otherDocuments' as DocumentSectionKey, title: 'Alte Documente' },
  ];

  const pmContractProject = useSelector((state: RootState) => state.models.pmContractProject);
  const addedDevices = useSelector((state: RootState) => state.models.pmContractProject.pmContractWorkPlaceItems);
  const interventionFileProjects = useSelector(
    (state: RootState) => state.models.pmContractProject.interventionFileProjects
  );
  const pmContractSectionFiles = useSelector(
    (state: RootState) => state.models.pmContractProject.pmContractSectionFiles || []
  );
  const statusConfig = useSelector((state: RootState) => state.store.pmContractProjectStatus.data);
  const { message } = App.useApp();

  const { data: _interventionFileProject, isPending: isLoadingInterventionFileProject } = useQuery({
    queryKey: ['interventionFileProject'],
    queryFn: () =>
      InterventionFileProjectService.search({
        pmContractId: pmContractProject.id,
        isWorkPlaceIncluded: true,
        isCompanyIncluded: true,
      }).then((data) => {
        dispatch(updateField({ field: 'interventionFileProjects', value: data?.response }));
        return data?.response[0];
      }),
    enabled: !!pmContractProject?.id,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const { profile } = useAuth();

  // Memoize devicesByWorkplace to prevent unnecessary recalculations
  const devicesByWorkplace = useMemo(() => {
    return addedDevices.reduce((acc: any, pmContractWorkPlaceItem: any) => {
      const workplaceName = pmContractWorkPlaceItem.workPlace?.name || 'Unknown';
      if (!acc[workplaceName]) {
        acc[workplaceName] = [];
      }
      acc[workplaceName].push(pmContractWorkPlaceItem);
      return acc;
    }, {});
  }, [addedDevices]);

  // Calculate transport details based on intervention file projects
  const calculateTransportDetails = useCallback(
    (workplace: string) => {
      const devices = devicesByWorkplace[workplace];
      if (!devices?.length) return null;

      // Get all intervention file projects for this workplace
      const workplaceProjects = interventionFileProjects.filter((i: any) => i.workPlace?.name === workplace);

      if (!workplaceProjects.length) return null;

      const displacements = workplaceProjects.length;

      // Get the cost per km for this workplace or use the global one
      const costPerKm = pmContractProject.costPerKilometer;

      // Sum up the actual traveled kilometers and time from intervention files
      const totalDistance = workplaceProjects.reduce(
        (sum: number, project: any) => sum + (2 * project.traveledKilometers || 0),
        0
      );

      const totalTime = workplaceProjects.reduce(
        (sum: number, project: any) =>
          sum + 2 * (project.timeTransportHours || 0) + 2 * (project.timeTransportMinutes || 0),
        0
      );
      const totalTimeInMinutes = workplaceProjects.reduce(
        (sum: number, project: any) =>
          sum + 2 * ((project.timeTransportHours || 0) * 60 + (project.timeTransportMinutes || 0)), // Convert to minutes
        0
      );

      // Convert totalTime to hours and minutes
      const hours = Math.floor(totalTimeInMinutes / 60);
      const minutes = totalTimeInMinutes % 60;
      const formattedTime = `${hours} ore ${minutes} minute`;

      // Calculate total cost
      const totalCost = totalDistance * costPerKm;

      return {
        distance: totalDistance,
        time: totalTime,
        cost: totalCost,
        displacements,
        costPerKm,
        perTrip: {
          distance: totalDistance,
          duration: totalTime,
          cost: totalCost,
          distanceText: `${totalDistance.toFixed(1)} km`,
          durationText: formattedTime, // Convert hours to minutes
        },
      };
    },
    [devicesByWorkplace, interventionFileProjects, pmContractProject.costPerKilometer]
  );

  // Fetch transport details when intervention file projects change
  useEffect(() => {
    if (Object.keys(devicesByWorkplace).length === 0 || interventionFileProjects.length === 0) return;

    setLoading(true);
    const details: Record<string, TransportDetail> = {};

    try {
      const workplaces = Object.keys(devicesByWorkplace);
      workplaces.forEach((workplace) => {
        const detail = calculateTransportDetails(workplace);
        if (detail) {
          details[workplace] = detail;
        }
      });

      setTransportDetails(details);
    } catch (error) {
      console.error('Error calculating transport details:', error);
      message.error('Eroare la calcularea detaliilor de transport');
    } finally {
      setLoading(false);
    }
  }, [devicesByWorkplace, interventionFileProjects, calculateTransportDetails]);

  // Calculate total costs
  const calculateTotalCosts = () => {
    const devicesCost = addedDevices.reduce(
      (acc, pmContractWorkPlaceItem) => acc + pmContractWorkPlaceItem.price + pmContractWorkPlaceItem.laborPrice,
      0
    );
    const laborCost = pmContractProject.pmContractLaborExtras.reduce(
      (acc, pmContractlaborExtra) => acc + pmContractlaborExtra.price * pmContractlaborExtra.piece,
      0
    );
    const transportCost = Object.keys(devicesByWorkplace).reduce((acc, workplace) => {
      return acc + transportDetails[workplace]?.cost || 0;
    }, 0);

    return {
      devicesCost,
      laborCost,
      transportCost,
      total: devicesCost + laborCost + transportCost,
    };
  };

  // Calculate progress percentage
  const calculateProgress = () => {
    const totalDevices = addedDevices.reduce((acc, pmContractWorkPlaceItem) => acc + pmContractWorkPlaceItem.piece, 0);
    const installedDevices = interventionFileProjects
      .flatMap((item: any) =>
        item.interventionFileProjectInstallations.map((installation: any) => installation.quantity)
      )
      .reduce((sum, qty) => sum + qty, 0);
    return Math.round((installedDevices / totalDevices) * 100) || 0;
  };

  // Calculate total progress data
  const totalProgressData = useMemo(() => {
    return Object.entries(devicesByWorkplace).map(([workplace, devices]: any) => {
      // Pre-filter projects for the current workplace once
      const workplaceProjects = interventionFileProjects.filter((i) => i.workPlace.name === workplace);

      // Pre-compute installations grouped by deviceTypeId for fast lookup
      const installationsByDevice = workplaceProjects
        .flatMap((item) => item.interventionFileProjectInstallations)
        .reduce((acc, installation) => {
          const { deviceTypeId, quantity } = installation;
          if (acc[deviceTypeId]) {
            acc[deviceTypeId] += quantity;
          } else {
            acc[deviceTypeId] = quantity;
          }
          return acc;
        }, {} as Record<string, number>);

      // Calculate total installed quantity for the workplace
      const totalInstalled = Object.values(installationsByDevice).reduce((sum: any, qty: any) => sum + qty, 0);

      return {
        key: workplace,
        workplace,
        toInstall: devices.reduce((acc: any, device: any) => acc + device.piece, 0),
        installed: totalInstalled,
        devices: devices.map((device: any) => {
          const installed = installationsByDevice[device.deviceTypeId] || 0;
          return {
            name: device.device.name,
            total: device.piece,
            stage: device?.pmContractWorkPlaceItemStage?.name,
            installed,
            remaining: device.piece - installed,
          };
        }),
      };
    });
  }, [devicesByWorkplace, interventionFileProjects]);

  // Calculate detailed progress data from intervention files
  const detailedProgressData = useMemo(() => {
    return interventionFileProjects.map((project) => ({
      key: project.id,
      date: project.deliveryDate,
      chiefTechnician: addedDevices.find((ad) => ad.workPlaceId == project.workPlaceId).chiefTehnician.name || '-',
      observations: project.observations || 'Nici un comentariu',
      image: project.attachedImage,
      hours: project?.hours,
      minutes: project?.minutes,
      installations: project.interventionFileProjectInstallations.map((ifp: any) => {
        return {
          key: ifp.id,
          workPlaceName: project.workPlace?.name || '-',
          stageName:
            addedDevices.find((ad) => ad.workPlaceId == project.workPlaceId).pmContractWorkPlaceItemStage?.name || '-',
          deviceName: ifp.deviceType.name,
          quantity: ifp.quantity,
          technicians: Array.from(
            new Set(
              ifp.interventionFileProjectInstallationTehnicians.map((technician: any) => `[${technician?.user?.name}]`)
            )
          ).join(' '),
        };
      }),
    }));
  }, [interventionFileProjects]);

  // Calculate equipment totals including labor time
  const equipmentTotals = useMemo(() => {
    return Object.entries(devicesByWorkplace).map(([workplace, pmContractWorkPlaceItems]: [string, any]) => {
      const workplaceTotal = pmContractWorkPlaceItems.reduce(
        (acc: any, pmContractWorkPlaceItem: any) => ({
          pieces: acc.pieces + pmContractWorkPlaceItem.piece,
          laborTimeMinutes: acc.laborTimeMinutes + pmContractWorkPlaceItem.laborTime,
        }),
        { pieces: 0, laborTimeMinutes: 0 }
      );

      return {
        workplace,
        pmContractWorkPlaceItems: pmContractWorkPlaceItems.map((pmContractWorkPlaceItem: any) => ({
          name: pmContractWorkPlaceItem.device.name,
          pieces: pmContractWorkPlaceItem.piece,
          laborTimeMinutes: pmContractWorkPlaceItem.laborTime,
          totalLaborTimeMinutes: pmContractWorkPlaceItem.laborTime,
        })),
        total: workplaceTotal,
      };
    });
  }, [devicesByWorkplace]);

  // Format minutes to hours and minutes
  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  // Function to create intervention file for a workplace
  const createInterventionFile = async () => {
    try {
      // Get workplace costs and details
      // const workplaceCosts = calculateTotalCosts();
      // const workplaceDevices = devicesByWorkplace[workplace];
      // const workplaceTransport = transportDetails[workplace];

      const interventionFileData = {
        pmContractId: pmContractProject.id,
        // pmContractProject: {
        //   id: pmContractProject.id,
        //   company: pmContractProject.company,
        //   technicians: pmContractProject.technician || [],
        //   status: pmContractProject.enumPmContractStatus,
        // },
        // devices: workplaceDevices,
        // costs: {
        //   devices: workplaceCosts.devicesCost,
        //   labor: workplaceCosts.laborCost,
        //   transport: workplaceTransport?.cost || 0,
        //   total: workplaceCosts.total,
        // },
        // transport: workplaceTransport,
        // statusConfig,
      };

      const queryParams = new URLSearchParams({
        data: JSON.stringify(interventionFileData),
      });

      window.open(`/project-management/interventionFileProject/new?${queryParams.toString()}`, '_blank');
    } catch (error) {
      console.error('Error creating intervention file:', error);
      message.error('Eroare la crearea fișei de intervenție');
    }
  };

  // Add effect to listen for intervention file updates
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'INTERVENTION_FILE_CREATED') {
        const newFile = event.data.data;

        // Update transport details
        if (newFile.transportDetails) {
          setTransportDetails((prev: any) => ({
            ...prev,
            [newFile.workplace.name]: {
              ...prev[newFile.workplace.name],
              displacements: prev[newFile.workplace.name].displacements + 1,
            },
          }));
        }

        // Show success message
        message.success('Fișa de intervenție a fost creată cu succes');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Helper function to get unique contact persons for a workplace
  const getWorkplaceContacts = (workplaceName: string): ContactPerson[] => {
    const devices = addedDevices.filter(
      (pmContractWorkPlaceItem) => pmContractWorkPlaceItem.workPlace?.name === workplaceName
    );
    const contactsMap = new Map();

    devices.forEach((pmContractWorkPlaceItem) => {
      if (pmContractWorkPlaceItem.agent) {
        const key = pmContractWorkPlaceItem.agent.id;
        contactsMap.set(key, {
          name: pmContractWorkPlaceItem.agent.name,
          role: pmContractWorkPlaceItem.agent.role[0],
          phone: pmContractWorkPlaceItem.agent.phone[0],
        });
      }
    });

    return Array.from(contactsMap.values());
  };

  const showModalImage = () => {
    setIsModalVisible(true);
  };

  const handleCancelModalImage = () => {
    setIsModalVisible(false);
  };

  // Initialize file lists from Redux when component mounts
  useEffect(() => {
    const newFileLists: Record<string, UploadFile<any>[]> = {
      projectOutline: [],
      situationPlan: [],
      otherDocuments: [],
    };

    // Group files by section
    pmContractSectionFiles.forEach((file: any) => {
      if (file.section && newFileLists[file.section]) {
        newFileLists[file.section].push({
          uid: file.id || file.uid,
          name: file.fileName,
          status: 'done',
          url: file.filePath,
        });
      }
    });

    setFileLists(newFileLists);
  }, [pmContractSectionFiles]);

  const uploadFiles = async (files: File[]): Promise<any[]> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    try {
      const response = await request(
        `/pmContract/upload`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('authToken'),
          },
          body: formData,
        },
        true
      );

      const data = await response.json();
      if (data?.errors?.length > 0) {
        message.error(data?.errors[0]);
        return [];
      } else {
        message.success(`File uploaded successfully!`);
        return data?.response;
      }
    } catch (error: any) {
      message.error(`Failed to upload files: ${error.message}`);
      return [];
    }
  };

  // Download files function
  const { mutate: downloadFile } = useMutation({
    mutationFn: (formData: any) => request(`/pmContract/download`, { method: 'POST', body: formData }),
    onSuccess: (response: any) => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'file.pdf';

      if (contentDisposition) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      return response.blob().then((blob: any) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      });
    },
    onError: (error: any) => {
      message.error('Eroare procesare request: ' + error.message);
    },
  });

  const handleDownloadFile = async (file: UploadFile) => {
    // Find the file in pmContractSectionFiles
    const sectionFile = pmContractSectionFiles.find((sf: any) => sf.fileName === file.name || sf.id === file.uid);

    if (sectionFile) {
      const formData = new FormData();
      formData.append('fileName', sectionFile.fileName);
      formData.append('fileData', sectionFile.filePath);
      formData.append('fileType', sectionFile.fileExtension);

      // Call your download function
      downloadFile(formData);
    } else {
      message.error('File not found.');
    }
  };

  // Create upload props for each document section
  const createUploadProps = (sectionKey: DocumentSectionKey) => ({
    multiple: true,
    showUploadList: true,
    fileList: fileLists[sectionKey],
    customRequest: async ({ file, onSuccess, onError }: any) => {
      try {
        const uploadedFiles = await uploadFiles([file]);
        if (uploadedFiles.length > 0) {
          const uploadedFile = {
            ...uploadedFiles[0],
            uid: file.uid,
            id: '', // Set id to null for new files
            fileName: uploadedFiles[0].name || file.name,
            filePath: uploadedFiles[0].url || '',
            fileExtension: file.name.split('.').pop(),
            section: sectionKey,
            pmContractId: pmContractProject.id || null,
          };

          // Update local state
          const updatedFileList = [
            ...fileLists[sectionKey],
            {
              uid: file.uid,
              name: uploadedFile.fileName,
              status: 'done',
              url: uploadedFile.filePath,
            },
          ];
          setFileLists((prev: any) => ({ ...prev, [sectionKey]: updatedFileList }));

          // Dispatch to Redux store
          dispatch(updatePmContractSectionFile(uploadedFile));

          onSuccess('ok');
        } else {
          onError('Upload failed');
        }
      } catch (error) {
        onError(error);
      }
    },
    onRemove: (file: UploadFile) => {
      // Update local state
      const updatedFileList = fileLists[sectionKey].filter((f) => f.uid !== file.uid);
      setFileLists((prev: any) => ({ ...prev, [sectionKey]: updatedFileList }));

      // Dispatch to Redux store
      dispatch(removePmContractSectionFile(file.uid as string));
    },
    beforeUpload: (file: any) => {
      const isAllowedType = [
        'image/jpg',
        'image/jpeg',
        'image/png',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ].includes(file.type);
      if (!isAllowedType) {
        message.error('You can only upload JPG, PNG images or PDF/DOC files!');
        return Upload.LIST_IGNORE;
      }
      return true;
    },
    itemRender: (_originNode: React.ReactNode, file: UploadFile) => (
      <div className='file-actions'>
        <Button type='link' icon={<DownloadOutlined />} onClick={() => handleDownloadFile(file)}>
          {file.name}
        </Button>
        <Tooltip title='Remove'>
          <DeleteOutlined
            onClick={() => {
              // Update local state
              const updatedFileList = fileLists[sectionKey].filter((f) => f.uid !== file.uid);
              setFileLists((prev) => ({ ...prev, [sectionKey]: updatedFileList }));

              // Dispatch to Redux store
              dispatch(removePmContractSectionFile(file.uid as string));
            }}
          />
        </Tooltip>
      </div>
    ),
  });

  if (isLoadingInterventionFileProject) {
    <Spin className='spinner' size='large' />;
  }

  return (
    <div className='raport-container'>
      {/* Status and Project Details Section */}
      <div className='top-section'>
        <Card
          title='Status Proiect'
          className='raport-card status-card'
          extra={
            <Button type='text' icon={<EyeOutlined />} onClick={() => setShowLegend(!showLegend)}>
              {showLegend ? 'Ascunde Legenda' : 'Vezi Legenda'}
            </Button>
          }
        >
          <div className='status-content'>
            <div className='current-status'>
              <div className='status-header'>
                <div
                  className='status-indicator'
                  style={{
                    backgroundColor:
                      statusConfig.find((s) => s.name === pmContractProject.enumPmContractProjectStatus?.name)
                        ?.color || '#d9d9d9',
                  }}
                />
                <div className='status-value'>
                  {pmContractProject.enumPmContractProjectStatus?.name || 'Nedefinit'}
                </div>
              </div>
              <div className='status-description'>
                {
                  statusConfig.find((s) => s.name === pmContractProject.enumPmContractProjectStatus?.name)
                    ?.description
                }
              </div>
            </div>

            {showLegend && (
              <div className='status-legend'>
                <div className='legend-grid'>
                  {statusConfig.map((status) => (
                    <div key={status.key} className='legend-item'>
                      <span className='legend-color' style={{ backgroundColor: status?.color }} />
                      <div className='legend-content'>
                        <div className='legend-name'>{status?.name}</div>
                        <div className='legend-description'>{status?.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>

        <Card title='Detalii Proiect' className='raport-card details-card'>
          <div className='project-details'>
            <div className='detail-item'>
              <strong>Beneficiar:</strong> {pmContractProject.company?.name}
            </div>
            <Divider />
            <div className='detail-item'>
              <strong>Locații și Persoane de Contact:</strong>
              <div className='table-container'>
                <Table
                  dataSource={Object.keys(devicesByWorkplace).flatMap((workplace) => {
                    const contacts = getWorkplaceContacts(workplace);
                    return contacts.length > 0
                      ? contacts.map((contact, index) => ({
                          key: `${workplace}-${index}`,
                          workplace,
                          name: contact.name,
                          role: contact.role,
                          phone: contact.phone,
                        }))
                      : [
                          {
                            key: workplace,
                            workplace,
                            name: '-',
                            role: '-',
                            phone: 'Nu există persoane de contact',
                          },
                        ];
                  })}
                  columns={[
                    {
                      title: 'Locație',
                      dataIndex: 'workplace',
                      key: 'workplace',
                    },
                    {
                      title: 'Nume Contact',
                      dataIndex: 'name',
                      key: 'name',
                    },
                    {
                      title: 'Rol',
                      dataIndex: 'role',
                      key: 'role',
                    },
                    {
                      title: 'Telefon',
                      dataIndex: 'phone',
                      key: 'phone',
                      render: (phone: string) =>
                        phone === 'Nu există persoane de contact' ? (
                          <span>{phone}</span>
                        ) : (
                          <Tag icon={<PhoneOutlined />}>{phone}</Tag>
                        ),
                    },
                  ]}
                  bordered
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                />
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Transport and Costs Section */}
      <div className='middle-section'>
        <Card title='Transport (dus - intors)' className='raport-card transport-card'>
          <div className='price-input-container' style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span className='detail-label'>Preț implicit per km:</span>
            <InputNumber
              min={0}
              value={pmContractProject.costPerKilometer}
              onChange={(value) => dispatch(updateField({ field: 'costPerKilometer', value: value }))}
              style={{ width: '80px' }}
            />
            <span className='detail-unit'>RON</span>
          </div>

          {loading ? (
            <div className='loading-container'>
              <Spin size='large' />
            </div>
          ) : (
            Object.entries(transportDetails).map(([workplace, details]) => {
              return (
                <div key={workplace} className='transport-details'>
                  <h4>
                    {workplace} - {devicesByWorkplace[workplace][0]?.workPlace?.address}
                  </h4>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <div className='detail-label'>Număr deplasări</div>
                      <div className='detail-value'>{details.displacements}</div>
                    </Col>
                    <Col span={24}>
                      <Divider style={{ margin: '12px 0' }} />
                    </Col>
                    <Col span={8}>
                      <div className='detail-label'>Total distanță</div>
                      <div className='detail-value highlight'>{details.distance.toFixed(1)} km</div>
                    </Col>
                    <Col span={8}>
                      <div className='detail-label'>Total timp</div>
                      <div className='detail-value highlight'>{details.perTrip.durationText}</div>
                    </Col>
                    <Col span={8}>
                      <div className='detail-label'>Total cost</div>
                      <div className='detail-value highlight'>{details.cost.toFixed(0)} RON</div>
                    </Col>
                  </Row>
                </div>
              );
            })
          )}
        </Card>

        <Card title='Costuri' className='raport-card costs-card'>
          {(() => {
            const costs = calculateTotalCosts();
            return (
              <div className='costs-content'>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <div className='cost-item'>
                      <div className='cost-label'>Cost dispozitive</div>
                      <div className='cost-value'>{costs.devicesCost.toFixed(2)} RON</div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className='cost-item'>
                      <div className='cost-label'>Cost extra manoperă</div>
                      <div className='cost-value'>{costs.laborCost.toFixed(2)} RON</div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className='cost-item'>
                      <div className='cost-label'>Cost total transport</div>
                      <div className='cost-value'>{costs.transportCost.toFixed(2)} RON</div>
                    </div>
                  </Col>
                  <Col span={12} className='total'>
                    <div className='cost-item-total'>
                      <div className='cost-label'>Cost total</div>
                      <div className='cost-value'>{costs.total.toFixed(2)} RON</div>
                    </div>
                  </Col>
                </Row>
              </div>
            );
          })()}
        </Card>
      </div>

      {/* Team Section */}
      <Card title='Echipa' className='raport-card'>
        <div className='table-container'>
          <Table
            bordered
            pagination={false}
            dataSource={Object.keys(devicesByWorkplace).map((workplace) => {
              // Get all devices for this workplace
              const workplaceDevices = devicesByWorkplace[workplace];

              // Find chief technician
              const chiefTechnician =
                workplaceDevices.find((device: any) => device.chiefTehnician)?.chiefTehnician?.name || '-';

              // Get all technicians for this workplace (without duplicates and excluding chief technician)
              const technicians = new Set<string>();
              workplaceDevices.forEach((device: any) => {
                if (
                  device.pmContractWorkPlaceItemTehnicians &&
                  Array.isArray(device.pmContractWorkPlaceItemTehnicians)
                ) {
                  device.pmContractWorkPlaceItemTehnicians.forEach((tech: any) => {
                    // Only add technician if they're not the chief technician
                    if (tech?.user?.name && tech?.user?.name !== chiefTechnician) {
                      technicians.add(tech?.user?.name);
                    }
                  });
                }
              });

              // Convert Set to Array for rendering
              const techniciansList = Array.from(technicians);

              return {
                key: workplace,
                workPlace: workplace,
                chiefTechnician: chiefTechnician,
                technicians: techniciansList,
              };
            })}
            columns={[
              { title: 'Locație', dataIndex: 'workPlace', key: 'workPlace' },
              {
                title: 'Tehnician Șef',
                dataIndex: 'chiefTechnician',
                key: 'chiefTechnician',
                render: (chiefTechnician) =>
                  chiefTechnician !== '-' ? <strong>{chiefTechnician}</strong> : <span>Nu este definit</span>,
              },
              {
                title: 'Tehnicieni',
                dataIndex: 'technicians',
                key: 'technicians',
                render: (technicians: string[]) =>
                  technicians && technicians.length > 0 ? (
                    <div>
                      {technicians.map((tech, index) => (
                        <Tag key={index} color='blue' style={{ margin: '2px' }}>
                          {tech}
                        </Tag>
                      ))}
                    </div>
                  ) : (
                    <span>Nu sunt tehnicieni asignați</span>
                  ),
              },
            ]}
            size='middle'
            rowClassName='workplace-row'
            scroll={{ x: 'max-content' }}
          />
        </div>
      </Card>

      {/* Progress Section with Progress Bar */}
      <Card title='Progres' className='raport-card'>
        <div className='progress-section'>
          <div className='progress-overview'>
            <div className='progress-header'>
              <h3>Progres Total Instalare</h3>
              <div className='progress-percentage'>{calculateProgress()}%</div>
            </div>

            <Progress
              percent={calculateProgress()}
              status='active'
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
            />

            <div className='progress-stats'>
              <div className='stat-item'>
                <span className='stat-label'>Total dispozitive</span>
                <span className='stat-value'>{addedDevices.reduce((acc, device) => acc + device?.piece, 0)}</span>
              </div>
              <div className='stat-item'>
                <span className='stat-label'>Instalate</span>
                <span className='stat-value'>
                  {interventionFileProjects
                    .flatMap((item: any) =>
                      item.interventionFileProjectInstallations.map((installation: any) => installation?.quantity)
                    )
                    .reduce((sum, qty) => sum + qty, 0)}
                </span>
              </div>
              <div className='stat-item'>
                <span className='stat-label'>Rămase</span>
                <span className='stat-value'>
                  {addedDevices.reduce((acc, device) => acc + device?.piece, 0) -
                    interventionFileProjects
                      .flatMap((item: any) =>
                        item.interventionFileProjectInstallations.map((installation: any) => installation?.quantity)
                      )
                      .reduce((sum, qty) => sum + qty, 0)}
                </span>
              </div>
            </div>
            <div className='progress-stats'>
              <div className='stat-item'>
                <span className='stat-label'>Manopera Total dispozitive</span>
                <span className='stat-value'>
                  {addedDevices.reduce((acc, device) => acc + device?.laborPrice, 0).toFixed(2)}
                </span>
              </div>

              <div className='stat-item'>
                <span className='stat-label'>Manopera Dispozitive Instalate</span>
                <span className='stat-value'>
                  {interventionFileProjects
                    .flatMap((item: any) =>
                      item.interventionFileProjectInstallations.map((installation: any) => {
                        const device = addedDevices.find((ad: any) => ad?.deviceTypeId === installation?.deviceTypeId);
                        return {
                          installation,
                          device,
                        };
                      })
                    )
                    .reduce(
                      (sum, { installation, device }) =>
                        sum + (device?.laborPrice / device?.piece) * installation?.quantity, // Calculate labor cost for each installation
                      0
                    )
                    .toFixed(2)}
                </span>
              </div>

              <div className='stat-item'>
                <span className='stat-label'>Manopera Dispozitive Rămase</span>
                <span className='stat-value'>
                  {addedDevices.reduce((acc, device) => acc + device?.laborPrice, 0) -
                    interventionFileProjects
                      .flatMap((item: any) =>
                        item.interventionFileProjectInstallations.map((installation: any) => {
                          const device = addedDevices.find(
                            (ad: any) => ad?.deviceTypeId === installation?.deviceTypeId
                          );
                          return {
                            installation,
                            device,
                          };
                        })
                      )
                      .reduce(
                        (sum, { installation, device }) =>
                          sum + (device?.laborPrice / device?.piece) * installation?.quantity, // Calculate labor cost for each installation
                        0
                      )
                      .toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <Collapse defaultActiveKey={['1', '2']}>
          <Panel header='Vezi Progresul Total' key='1'>
            <div className='table-container'>
              <Table
                dataSource={totalProgressData.flatMap((workplace) =>
                  workplace.devices.map((device: any) => ({
                    key: `${workplace.workplace}-${device.name}`,
                    workplace: workplace.workplace,
                    deviceName: device.name,
                    stage: device.stage,
                    total: device.total,
                    installed: device.installed,
                    remaining: device.remaining,
                  }))
                )}
                columns={[
                  {
                    title: 'Locație',
                    dataIndex: 'workplace',
                    key: 'workplace',
                  },
                  {
                    title: 'Etapa',
                    dataIndex: 'stage',
                    key: 'stage',
                  },
                  {
                    title: 'Dispozitiv',
                    dataIndex: 'deviceName',
                    key: 'deviceName',
                    ellipsis: true,
                    render: (text) => (
                      <Tooltip title={text}>
                        <div
                          style={{
                            maxWidth: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {text}
                        </div>
                      </Tooltip>
                    ),
                  },
                  {
                    title: 'Total',
                    dataIndex: 'total',
                    key: 'total',
                  },
                  {
                    title: 'Instalate',
                    dataIndex: 'installed',
                    key: 'installed',
                  },
                  {
                    title: 'Rămase',
                    dataIndex: 'remaining',
                    key: 'remaining',
                  },
                ]}
                bordered
                pagination={false}
                scroll={{ x: 'max-content' }}
              />
            </div>
          </Panel>

          <Panel header='Vezi Progresul Detaliat' key='2'>
            {detailedProgressData
              .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()) // Sort by date, most recent first
              .map((project: any, projectIndex: number) => (
                <div
                  key={project.key}
                  style={{
                    marginBottom: '20px',
                    padding: '15px',
                    borderRadius: '8px',
                    backgroundColor: projectIndex % 2 === 0 ? '#f0f7ff' : '#f6ffed',
                    border: `1px solid ${projectIndex % 2 === 0 ? '#91caff' : '#b7eb8f'}`,
                  }}
                >
                  <h3 style={{ color: projectIndex % 2 === 0 ? '#1677ff' : '#52c41a' }}>
                    Data: {moment(project.date).format('DD-MM-YYYY')}
                  </h3>
                  <p>
                    <strong>Sef Lucrare:</strong> {project.chiefTechnician}
                  </p>
                  <p>
                    <strong>Comentariu Sef Lucrare:</strong> {project.observations}
                  </p>
                  <p>
                    <strong>Durata interventie:</strong> {project?.hours} ore {project?.minutes} minute
                  </p>
                  <p>
                    {!!project.image ? (
                      <div>
                        <strong>Imagine: </strong> {/* Eye Icon for preview */}
                        <a
                          onClick={showModalImage}
                          style={{
                            color: projectIndex % 2 === 0 ? '#1677ff' : '#52c41a',
                            cursor: 'pointer',
                            fontSize: '20px',
                            transition: 'color 0.3s',
                            margin: '0px 10px',
                          }}
                          title='Vezi Imaginea'
                        >
                          <EyeOutlined />
                        </a>
                        {/* Download Icon */}
                        <a
                          href={'data:image/png;base64,' + project.image}
                          download={`Imagine-${project.key}.png`}
                          style={{
                            color: projectIndex % 2 === 0 ? '#1677ff' : '#52c41a',
                            cursor: 'pointer',
                            fontSize: '20px',
                            transition: 'color 0.3s',
                          }}
                          title='Descarcă Imaginea'
                        >
                          <DownloadOutlined />
                        </a>
                        <Modal
                          open={isModalVisible}
                          onCancel={handleCancelModalImage}
                          footer={null}
                          centered
                          bodyStyle={{ padding: 0 }}
                        >
                          <Image
                            width='100%'
                            src={`data:image/png;base64,${project.image}`}
                            alt='Preview'
                            style={{
                              objectFit: 'contain', // Ensures image maintains aspect ratio
                            }}
                          />
                        </Modal>
                      </div>
                    ) : (
                      <p>
                        <strong>Imagine:</strong> Nici o imagine incarcata
                      </p>
                    )}
                  </p>
                  <div className='table-container'>
                    <Table
                      dataSource={project.installations}
                      columns={[
                        {
                          title: 'Locație',
                          dataIndex: 'workPlaceName',
                          key: 'workPlaceName',
                          render: (text) => <span style={{ fontWeight: 'bold' }}>{text}</span>,
                        },
                        {
                          title: 'Etapa',
                          dataIndex: 'stageName',
                          key: 'stageName',
                          render: (text) => <Tag color={projectIndex % 2 === 0 ? 'blue' : 'green'}>{text}</Tag>,
                        },
                        {
                          title: 'Tehnician',
                          dataIndex: 'technicians',
                          key: 'technicians',
                          render: (text) => (
                            <div
                              style={{
                                backgroundColor: projectIndex % 2 === 0 ? '#e6f4ff' : '#f6ffed',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                border: `1px solid ${projectIndex % 2 === 0 ? '#91caff' : '#b7eb8f'}`,
                              }}
                            >
                              {text}
                            </div>
                          ),
                        },
                        {
                          title: 'Dispozitiv',
                          dataIndex: 'deviceName',
                          key: 'deviceName',
                          ellipsis: true,
                          render: (text) => (
                            <Tooltip title={text}>
                              <div
                                style={{
                                  maxWidth: 600,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  fontStyle: 'italic',
                                  color: projectIndex % 2 === 0 ? '#0958d9' : '#389e0d',
                                }}
                              >
                                {text}
                              </div>
                            </Tooltip>
                          ),
                        },
                        {
                          title: 'Cantitate Instalată',
                          dataIndex: 'quantity',
                          key: 'quantity',
                          render: (qty) => (
                            <Tag
                              color={projectIndex % 2 === 0 ? 'blue' : 'green'}
                              style={{
                                fontWeight: 'bold',
                                fontSize: '14px',
                                padding: '2px 10px',
                              }}
                            >
                              {qty} buc
                            </Tag>
                          ),
                        },
                      ]}
                      pagination={false}
                      bordered
                      rowClassName={(_record, index) =>
                        index % 2 === 0
                          ? projectIndex % 2 === 0
                            ? 'even-row-blue'
                            : 'even-row-green'
                          : projectIndex % 2 === 0
                          ? 'odd-row-blue'
                          : 'odd-row-green'
                      }
                      className='detailed-progress-table'
                      scroll={{ x: 'max-content' }}
                    />
                  </div>
                </div>
              ))}
          </Panel>
        </Collapse>
      </Card>

      {/* Equipment Section */}
      <Card className='raport-card'>
        {/* Title and Button on the same line */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={5} style={{ margin: 0 }}>
            Echipamente Adaugate
          </Title>
          {!!pmContractProject?.id && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Button
                type='primary'
                icon={<FileAddOutlined />}
                disabled={!addedDevices?.some((ad: any) => ad?.chiefTehnicianId === profile?.id)}
                onClick={(e) => {
                  e.stopPropagation();
                  createInterventionFile();
                }}
              >
                Fisa sefului de echipa
              </Button>
            </div>
          )}
        </div>

        {/* Total General Manoperă (Aligned right) */}
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Title level={5} style={{ margin: 0 }}>
            Total timp manoperă:{' '}
            {formatTime(equipmentTotals.reduce((acc, { total }) => acc + total.laborTimeMinutes, 0))}
          </Title>
        </div>

        <Collapse>
          {equipmentTotals.map(({ workplace, pmContractWorkPlaceItems: devices, total }) => (
            <Panel
              header={
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <span>{workplace}</span>
                  <Tag color='blue'>Total timp manoperă: {formatTime(total.laborTimeMinutes)}</Tag>
                </Space>
              }
              key={workplace}
            >
              <div className='table-container'>
                <Table
                  dataSource={devices}
                  columns={[
                    {
                      title: 'Dispozitiv',
                      dataIndex: 'name',
                      key: 'name',
                      ellipsis: true,
                      render: (text) => (
                        <Tooltip title={text}>
                          <div
                            style={{
                              maxWidth: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {text}
                          </div>
                        </Tooltip>
                      ),
                    },
                    {
                      title: 'Cantitate',
                      dataIndex: 'pieces',
                      key: 'pieces',
                    },
                    {
                      title: 'Timp Manoperă',
                      dataIndex: 'laborTimeMinutes',
                      key: 'laborTimeMinutes',
                      render: (minutes) => `${minutes} minute`,
                    },
                    {
                      title: 'Total Manoperă',
                      dataIndex: 'totalLaborTimeMinutes',
                      key: 'totalLaborTimeMinutes',
                      render: (minutes) => formatTime(minutes),
                    },
                  ]}
                  bordered
                  pagination={false}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>{total.pieces}</Table.Summary.Cell>
                      <Table.Summary.Cell index={2}></Table.Summary.Cell>
                      <Table.Summary.Cell index={3}>{formatTime(total.laborTimeMinutes)}</Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                  scroll={{ x: 'max-content' }}
                />
              </div>
            </Panel>
          ))}
        </Collapse>
      </Card>

      {/* Documents Section */}
      <Card title='Documente' className='raport-card'>
        <Collapse>
          {documentSections.map((section) => (
            <Panel header={section.title} key={section.key}>
              <Dragger {...createUploadProps(section.key)}>
                <p className='ant-upload-drag-icon'>
                  <InboxOutlined />
                </p>
                <p className='ant-upload-text'>Click sau trage fisiere pentru incarcare</p>
                <p className='ant-upload-hint'>Supports JPG, PNG, PDF, DOC files.</p>
              </Dragger>
            </Panel>
          ))}
        </Collapse>
      </Card>
    </div>
  );
};

export default RaportTab;
